import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import Text from 'shopper/components/Text';

import TextWithRichContent from 'components/TextWithRichContent';

import { getTimeTo } from 'lib/date';

import OfferAuthors from '../Main/Authors';

/**
 * Altura máxima (em px) das instruções antes de virar colapsável. Acima
 * disso mostramos o botão "Ver mais" pra não empurrar a lista de
 * comentários pra baixo com textos ou cards de cupom muito grandes.
 */
const COLLAPSED_INSTRUCTIONS_MAX_HEIGHT = 120;

const OfferAuthorsCard = ({
  authorCount,
  authors,
  offerId,
  offerInstructions,
  offerPublished,
  userId,
  userName,
  userSpecialtyName,
  userUsername,
}) => {
  const instructionsRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (!offerInstructions || !instructionsRef.current) {
      return;
    }

    setIsOverflowing(
      instructionsRef.current.scrollHeight >
        COLLAPSED_INSTRUCTIONS_MAX_HEIGHT + 2
    );
  }, [offerInstructions]);

  const shouldClamp = isOverflowing && !isExpanded;

  return (
    <div className="shrink-0 rounded-3 border border-neutral-high-300 p-3 dark:border-neutral-low-300">
      <div className="flex items-start justify-between gap-x-2">
        <OfferAuthors
          authorCount={authorCount}
          authors={authors}
          offerId={offerId}
          userId={userId}
          userName={userName}
          userSpecialtyName={userSpecialtyName}
          userUsername={userUsername}
        />
        {offerPublished && (
          <Text
            as="span"
            className="shrink-0 whitespace-nowrap text-neutral-low-300 dark:text-neutral-high-400"
            size="size3"
          >
            {getTimeTo(offerPublished)}
          </Text>
        )}
      </div>
      {offerInstructions && (
        <>
          <div
            ref={instructionsRef}
            className={twJoin('overflow-hidden')}
            style={
              shouldClamp
                ? { maxHeight: COLLAPSED_INSTRUCTIONS_MAX_HEIGHT }
                : undefined
            }
          >
            <TextWithRichContent
              className="mt-2 text-sm text-neutral-low-200 dark:text-neutral-high-100"
              full
            >
              {offerInstructions}
            </TextWithRichContent>
          </div>
          {isOverflowing && (
            <button
              aria-expanded={isExpanded}
              className="mt-2 text-sm font-bold text-primary-300 hover:underline"
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded ? 'Ver menos' : 'Ver mais'}
            </button>
          )}
        </>
      )}
    </div>
  );
};

OfferAuthorsCard.propTypes = {
  authors: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  authorCount: PropTypes.number.isRequired,
  offerId: PropTypes.number.isRequired,
  offerInstructions: PropTypes.string,
  offerPublished: PropTypes.string,
  userId: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userUsername: PropTypes.string.isRequired,
};

export default OfferAuthorsCard;
